import { MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Input, Modal, Select, Space } from "antd";
import { changeTicketStatus, removeAllTicketEvents, TicketCategoriesAssemble } from "../../../utils";
import { useSelector } from "react-redux";
import { AuthenticationState } from "../../../interfaces/users";
import { useQueries } from "react-query";
import { api } from "../../../config";
import { isSharedComponentDisabled, ModulePermission, userHasPermission } from "../../../utils/helpers/userHelpers";
import { duplicateFieldReportTicket } from "../../../utils/helpers/ticketHelpers";
import { Content } from "antd/es/layout/layout";
import { useModal } from "../../../utils/hooks/useModal";
import { useState } from "react";
import { toast } from "react-toastify";

type AssembleStatusType =  | "ASSEMBLE_PENDING_SCHEDULE"
      | "ASSEMBLE_PENDING"
      | "ASSEMBLE_WAITING_ANALYSIS"
      | "ASSEMBLE_REJECTED"
      | "ASSEMBLE_WAITING_PARTS"
      | "ASSEMBLE_TECHNICAL_ASSISTANCE"
      | "ASSEMBLE_APPROVED"
      | "ASSEMBLE_FINALIZED"
      | "ASSEMBLE_CANCELLED"

type AssembleUnavailableStatus = "BUILDING_IN_PROGRESS" | "UNAVAILABLE_FOR_ASSEMBLE";

function ButtonMore({
  ticket,
  ticketId,
  disabled = false,
  type = "assemble",
  currentPriority,
  currentPriorityReason,
  currentStatus,
  isUnavailableForAssemble,
  assembleUnavailableStatus,
  assembleUnavailableReason,
}: any) {
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const { isOpen, onOpen, onClose } = useModal()
  const {
    isOpen: isUnavailableModalOpen,
    onOpen: onOpenUnavailableModal,
    onClose: onCloseUnavailableModal,
  } = useModal();
  const [{ refetch: ticketRefetch }, { refetch: boardRefetch }] = useQueries([
    {
      queryKey: "tickets/assemble",
      queryFn: () => api(token).get(`ticket/assemble/${ticketId}`),
      refetchOnMount: false,
    },
    {
      queryKey: "tickets/assemble/board",
      queryFn: () => api(token).get(`tickets/assemble/board`),
      refetchOnMount: false,
    },
  ]);

  const handleOnClick = async (
    status:
     AssembleStatusType
  ) => {
    await changeTicketStatus(status, ticketId, token, "assemble", true);
    boardRefetch();
    ticketRefetch();
  };

  const handleRemoveEvents = async () => removeAllTicketEvents(ticketId, token);
  

  const handleDuplicate = async () => {
    if (
      window.confirm(
        "Tem certeza de que deseja duplicar este ticket de medição?"
      )
    ) {
      await duplicateFieldReportTicket(ticketId, token);
      boardRefetch();
    }
  };

  const [priority, setPriority] = useState(currentPriority)
  const [priorityReason, setPriorityReason] = useState(currentPriorityReason ?? '')
  const [loading, setLoading] = useState(false)
  const [unavailableStatus, setUnavailableStatus] =
    useState<AssembleUnavailableStatus>(
      assembleUnavailableStatus ?? "BUILDING_IN_PROGRESS"
    );
  const [unavailableReason, setUnavailableReason] = useState(
    assembleUnavailableReason ?? ""
  );
  const [unavailableLoading, setUnavailableLoading] = useState(false);
  const handleSubmitPriorityChange = async () => {
    
    setLoading(true)
    try {
      await api(token).put(`/ticket/assemble/${ticketId}`, {
        priority,
        priorityReason
      })
      toast.success("Prioridade alterada com sucesso!")
      ticketRefetch()
      boardRefetch()
      setPriorityReason('')
    } catch (error) {
      toast.error('Ocorreu um erro ao alterar a prioridade do Ticket')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setPriority(currentPriority)
    setPriorityReason(currentPriorityReason)
    onClose()
  }

  const handleUnavailableClose = () => {
    setUnavailableStatus(assembleUnavailableStatus ?? "BUILDING_IN_PROGRESS");
    setUnavailableReason(assembleUnavailableReason ?? "");
    onCloseUnavailableModal();
  };

  const handleSubmitUnavailableForAssemble = async () => {
    if (!unavailableReason.trim()) {
      toast.error("Informe o motivo");
      return;
    }

    setUnavailableLoading(true);
    try {
      await api(token).put(`/ticket/assemble/${ticketId}`, {
        isUnavailableForAssemble: true,
        assembleUnavailableStatus: unavailableStatus,
        assembleUnavailableReason: unavailableReason,
      });
      await api(token).post(`comments`, {
        content: `Marcado como Cliente indisponível para montagem: ${unavailableReason}`,
        userId: user?.id,
        fieldReportId: ticketId,
      });
      toast.success("Ticket marcado como Cliente indisponível para montagem");
      ticketRefetch();
      boardRefetch();
      onCloseUnavailableModal();
    } catch (error) {
      toast.error(
        "Ocorreu um erro ao marcar o Ticket como Cliente indisponível para montagem"
      );
    } finally {
      setUnavailableLoading(false);
    }
  };

  const handleClearUnavailableForAssemble = async () => {
    setUnavailableLoading(true);
    try {
      await api(token).put(`/ticket/assemble/${ticketId}`, {
        isUnavailableForAssemble: false,
        assembleUnavailableStatus: null,
        assembleUnavailableReason: null,
      });
      toast.success("Marcação removida com sucesso!");
      ticketRefetch();
      boardRefetch();
    } catch (error) {
      toast.error("Ocorreu um erro ao remover a marcação do Ticket");
    } finally {
      setUnavailableLoading(false);
    }
  };

  const items = [
    {
      key: "1",
      label: " Reiniciar Ticket",
      disabled: [7, 8, 9, 10].includes(currentStatus),
      onClick: async () => {
        await handleRemoveEvents();
        await api(token).put(`ticket/assemble/${ticketId}`, {
        status: "ASSEMBLE_PENDING_SCHEDULE",
        cancellationReason: null,

        ...(ticket?.cancellationImages
          ? {
              cancellationImages: {
                delete: ticket.cancellationImages.map((image: any) => ({
                  id: image.id,
                })),
              },
            }
          : {}),
      });
      boardRefetch();
      ticketRefetch();
      },
    },
    {
      key: "2",
      label: "Alterar Prioridade",
      disabled: !userHasPermission(user, { management: ModulePermission.ADMIN }),
      onClick: onOpen,
    },
    {
      key: "4",
      label: isUnavailableForAssemble
        ? 'Desmarcar "Cliente indisponível para montagem"'
        : "Marcar Como Cliente indisponível para montagem",
      onClick: () => {
        if (isUnavailableForAssemble) {
          Modal.confirm({
            title: 'Desmarcar "Cliente indisponível para montagem"',
            content:
              "Tem certeza que deseja limpar a marcação e o motivo deste ticket?",
            okText: "Desmarcar",
            cancelText: "Cancelar",
            onOk: handleClearUnavailableForAssemble,
          });
          return;
        }
        onOpenUnavailableModal();
      },
    },
    {
      key: "3",
      label: "Cancelar",
      danger: true,
      onClick: () => handleOnClick("ASSEMBLE_CANCELLED"),
    },
  ];

  return (
    <>
      <Modal
        onCancel={handleUnavailableClose}
        onClose={handleUnavailableClose}
        open={isUnavailableModalOpen}
        okButtonProps={{ loading: unavailableLoading }}
        onOk={handleSubmitUnavailableForAssemble}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Content title="Marcar Como Cliente indisponível para montagem">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col w-full">
              <label>Situação:</label>
              <Select
                value={unavailableStatus}
                onChange={(value) => setUnavailableStatus(value)}
              >
                <Select.Option value="BUILDING_IN_PROGRESS">
                  Cliente Em Obras
                </Select.Option>
                <Select.Option value="UNAVAILABLE_FOR_ASSEMBLE">
                  Indisponível para montagem
                </Select.Option>
              </Select>
            </div>
            <div className="flex flex-col w-full">
              <label>Motivo:</label>
              <Input
                type="text"
                value={unavailableReason}
                onChange={(e) => setUnavailableReason(e.target.value)}
              />
            </div>
          </div>
        </Content>
      </Modal>
      <Modal onCancel={handleClose} onClose={handleClose} open={isOpen} okButtonProps={{loading}} onOk={handleSubmitPriorityChange}>
        <Content title="Alterar Prioridade">
          <div className='flex flex-col gap-3'>
            <div className="flex flex-col w-full">
              <label>Prioridade:</label>
              <Select value={priority} onChange={(e) => setPriority(e)}>
                <Select.Option value={1}>Alta</Select.Option>
                <Select.Option value={2}>Normal</Select.Option>
              </Select>
            </div>
            <div className="flex flex-col w-full">
              <label>Motivo:</label>
              <Input
                type="text"
                value={priorityReason}
                onChange={(e) => setPriorityReason(e.target.value)}
              />
            </div>
          </div>
        </Content>
      </Modal>
      <Space className="absolute right-0 top-2">
        <Dropdown
          disabled={isSharedComponentDisabled(type, user, disabled)}
          menu={{ items }}
          placement="bottomLeft"
        >
          <Button
            disabled={isSharedComponentDisabled(type, user, disabled)}
            size="small"
            shape="circle"
          >
            <MoreOutlined />
          </Button>
        </Dropdown>
      </Space>
    </>
  );
}
export default ButtonMore;
